import { useEffect, useState } from 'react';
import { FiCheck, FiClock, FiX, FiMinus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import http from '../../api/http';
import { useAuth } from '../../context/AuthContext';
import './Account.css';

export default function AccountProfile() {
  const { refreshMe } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgTone, setMsgTone] = useState('ok');

  const [wsForm, setWsForm] = useState({
    companyName: '',
    inn: '',
    kpp: '',
    address: '',
    comment: '',
  });
  const [wsSaving, setWsSaving] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);
  const [savingMarketing, setSavingMarketing] = useState(false);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await http.get('/api/profile');
        if (!ok) return;
        setProfile(data);
        setName(data.name || '');
        setMarketingOn(Boolean(data.marketingConsent));
        setWsForm({
          companyName: data.wholesale?.companyName || '',
          inn: data.wholesale?.inn || '',
          kpp: data.wholesale?.kpp || '',
          address: data.wholesale?.address || '',
          comment: data.wholesale?.comment || '',
        });
      } catch {
        /* 401 → RequireAuth */
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    setMsg('');
    try {
      await http.patch('/api/profile', { name });
      await refreshMe();
      setMsgTone('ok');
      setMsg('Имя обновлено');
    } catch (err) {
      setMsgTone('bad');
      setMsg(err.response?.data?.error || 'Ошибка сохранения имени');
    } finally {
      setSavingName(false);
    }
  };

  const saveMarketing = async (next) => {
    setSavingMarketing(true);
    setMsg('');
    try {
      await http.patch('/api/profile', { marketingConsent: next });
      setMarketingOn(next);
      setMsgTone('ok');
      setMsg(next ? 'Согласие на рассылки сохранено' : 'Согласие на рассылки отозвано');
    } catch (err) {
      setMsgTone('bad');
      setMsg(err.response?.data?.error || 'Не удалось сохранить согласие');
    } finally {
      setSavingMarketing(false);
    }
  };

  const sendWholesale = async (e) => {
    e.preventDefault();
    setWsSaving(true);
    setMsg('');
    try {
      const { data } = await http.post('/api/profile/wholesale-request', wsForm);
      setProfile((p) => ({ ...p, wholesale: data.wholesale }));
      setMsgTone('ok');
      setMsg('Заявка на опт отправлена');
      await refreshMe();
    } catch (err) {
      setMsgTone('bad');
      setMsg(err.response?.data?.error || 'Не удалось отправить заявку');
    } finally {
      setWsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="account-state">
        <div className="account-skeleton" />
      </div>
    );
  }

  if (!profile) {
    return (
      <section className="account-card">
        <h2 className="account-card__title">Профиль</h2>
        <p className="account-card__hint">Не удалось получить профиль</p>
      </section>
    );
  }

  const ws = profile.wholesale || { status: 'none' };
  const canRequest = !ws.status || ws.status === 'none' || ws.status === 'declined';

  return (
    <div className="account-stack">
      {msg && (
        <div className={`account-toast account-toast--${msgTone}`} role="status">
          {msg}
        </div>
      )}

      <section className="account-card">
        <div className="account-card__head">
          <h2 className="account-card__title">Профиль</h2>
          <p className="account-card__hint">Основные данные аккаунта</p>
        </div>

        <form onSubmit={saveName} className="account-form">
          <label className="account-field">
            <span>Имя</span>
            <div className="account-field__row">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Как к вам обращаться"
              />
              <button className="account-btn" disabled={savingName} type="submit">
                {savingName ? 'Сохраняю…' : 'Сохранить'}
              </button>
            </div>
          </label>
        </form>

        <dl className="account-meta">
          <div>
            <dt>Телефон</dt>
            <dd>{profile.phone}</dd>
          </div>
          <div>
            <dt>Роль</dt>
            <dd>{roleLabel(profile.role)}</dd>
          </div>
          <div>
            <dt>Опт</dt>
            <dd>{renderWholesale(ws)}</dd>
          </div>
        </dl>
      </section>

      <section className="account-card">
        <div className="account-card__head">
          <h2 className="account-card__title">Рассылки</h2>
          <p className="account-card__hint">
            Реклама и акции по email, SMS и мессенджерам — только с этого согласия. Заказы и чеки
            приходят и без него.
          </p>
        </div>
        <label className="account-check">
          <input
            type="checkbox"
            checked={marketingOn}
            disabled={savingMarketing}
            onChange={(e) => saveMarketing(e.target.checked)}
          />
          <span>Согласен получать рекламные и информационные сообщения</span>
        </label>
      </section>

      {canRequest && (
        <section className="account-card">
          <div className="account-card__head">
            <h2 className="account-card__title">Заявка на оптовые цены</h2>
            <p className="account-card__hint">
              Менеджер проверит реквизиты (наименование, ИНН, КПП, адрес) и откроет оптовый прайс.
              Эти сведения обрабатываются как данные представителя / контрагента для заключения
              договора — см. <Link to="/privacy">политику конфиденциальности</Link>.
            </p>
          </div>

          <form onSubmit={sendWholesale} className="account-form account-form--grid">
            <label className="account-field">
              <span>Компания *</span>
              <input
                required
                value={wsForm.companyName}
                onChange={(e) => setWsForm((p) => ({ ...p, companyName: e.target.value }))}
              />
            </label>
            <label className="account-field">
              <span>ИНН *</span>
              <input
                required
                value={wsForm.inn}
                onChange={(e) => setWsForm((p) => ({ ...p, inn: e.target.value }))}
              />
            </label>
            <label className="account-field">
              <span>КПП</span>
              <input
                value={wsForm.kpp}
                onChange={(e) => setWsForm((p) => ({ ...p, kpp: e.target.value }))}
              />
            </label>
            <label className="account-field">
              <span>Адрес</span>
              <input
                value={wsForm.address}
                onChange={(e) => setWsForm((p) => ({ ...p, address: e.target.value }))}
              />
            </label>
            <label className="account-field account-field--full">
              <span>Комментарий</span>
              <input
                value={wsForm.comment}
                onChange={(e) => setWsForm((p) => ({ ...p, comment: e.target.value }))}
              />
            </label>
            <div className="account-form__actions account-field--full">
              <button className="account-btn" type="submit" disabled={wsSaving}>
                {wsSaving ? 'Отправляю…' : 'Отправить заявку'}
              </button>
            </div>
          </form>
        </section>
      )}

      {ws.status === 'pending' && (
        <section className="account-card account-card--soft">
          <div className="account-card__head">
            <h2 className="account-card__title">Заявка на модерации</h2>
            <p className="account-card__hint">Обычно отвечаем в рабочее время.</p>
          </div>
        </section>
      )}
    </div>
  );
}

function roleLabel(role) {
  if (role === 'admin') return 'Администратор';
  if (role === 'manager') return 'Менеджер';
  if (role === 'wholesale') return 'Опт';
  return 'Покупатель';
}

function renderWholesale(w) {
  if (!w || w.status === 'none') {
    return (
      <span className="account-chip">
        <FiMinus /> Нет
      </span>
    );
  }
  if (w.status === 'pending') {
    return (
      <span className="account-chip account-chip--warn">
        <FiClock /> На модерации
      </span>
    );
  }
  if (w.status === 'approved') {
    return (
      <span className="account-chip account-chip--ok">
        <FiCheck /> Подтверждён
      </span>
    );
  }
  if (w.status === 'declined') {
    return (
      <span className="account-chip account-chip--bad">
        <FiX /> Отклонён
      </span>
    );
  }
  return <span className="account-chip">{w.status}</span>;
}
